import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { MasterDataProvider } from './contexts/MasterDataContext';
import { CustomerAuthProvider } from './contexts/CustomerAuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { CustomerProtectedRoute } from './components/CustomerProtectedRoute';
import CustomerNavBar from './components/CustomerNavBar';
import { Toaster } from './components/ui/sonner';
import { PWAInstallPrompt } from './components/PWAInstallPrompt';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import CalendarViewPage from './pages/CalendarViewPage';
import WhatsAppBookingPage from './pages/WhatsAppBookingPage';
import WhatsAppPayPage from './pages/play/WhatsAppPayPage';
import LoginLogsPage from './pages/LoginLogsPage';
import DashboardPage from './pages/DashboardPage';
import NewBookingPage from './pages/NewBookingPage';
import BookingsListPage from './pages/BookingsListPage';
import ReportsPage from './pages/ReportsPage';
import EmailBookingPage from './pages/EmailBookingPage';
import SlotFinderPage from './pages/SlotFinderPage';
import SlotInventoryPage from './pages/SlotInventoryPage';
import ContactsPage from './pages/ContactsPage';
import AnalysisPage from './pages/AnalysisPage';
import UsersPage from './pages/UsersPage';
import VenueCourtMasterPage from './pages/VenueCourtMasterPage';
import PricingDashboardPage from './pages/PricingDashboardPage';
import WorkingHoursPage from './pages/WorkingHoursPage';
import CouponManagementPage from './pages/CouponManagementPage';
import AnnouncementsPage from './pages/AnnouncementsPage';
import SettingsPage from './pages/SettingsPage';
import TournamentManagementPage from './pages/TournamentManagementPage'; // DEPRECATED — kept to avoid import error until file is deleted
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import WhatsAppAnalyticsPage from './pages/WhatsAppAnalyticsPage';
import WhatsAppConversationsPage from './pages/WhatsAppConversationsPage';
import TemplateLogsPage from './pages/TemplateLogsPage';
import OccupancyPage from './pages/OccupancyPage';
import SlotBookingPage from './pages/SlotBookingPage';
import VenueAliasesPage from './pages/VenueAliasesPage';
import DayBookEntryPage from './pages/DayBookEntryPage';
import DailySalesReportPage from './pages/DailySalesReportPage';
import CallingDataPage from './pages/CallingDataPage';
import PosPlayersPage from './pages/PosPlayersPage';
import ExpensesPage from './pages/ExpensesPage';
import FundsPage from './pages/FundsPage';
import VenueEventsPage from './pages/VenueEventsPage';
import AppUsersPage from './pages/AppUsersPage';
import PublicExpenseApprovalPage from './pages/PublicExpenseApprovalPage';
import CoachingPage from './pages/CoachingPage';
import SchoolPage from './pages/SchoolPage';
import ReviewLogPage from './pages/ReviewLogPage';
import PublicApiLogsPage from './pages/PublicApiLogsPage';
import CancellationsPage from './pages/CancellationsPage';
import MarketingPage from './pages/MarketingPage';
import WaCampaignPage from './pages/WaCampaignPage';
import VoiceApiPage from './pages/VoiceApiPage';
import EventsPage from './pages/EventsPage';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import MgmtDashboardPage from './pages/MgmtDashboardPage';
import DutyRosterPage from './pages/DutyRosterPage';
import PnLPage from './pages/PnLPage';
import { trackPageView, trackClick } from './utils/analytics';
import EventPlayersPage from './pages/EventPlayersPage';
import PlayerEventsPage from './pages/play/PlayerEventsPage';
import PlayerEventDetailPage from './pages/play/PlayerEventDetailPage';
import EventPaymentStatusPage from './pages/play/EventPaymentStatusPage';
import MyRegistrationsPage from './pages/play/MyRegistrationsPage';
import PublicEventLandingPage from './pages/play/PublicEventLandingPage';
import TournamentHubPage from './pages/hub/TournamentHubPage';
import TournamentHubSubmitPage from './pages/hub/TournamentHubSubmitPage';
import TournamentHubDetailPage from './pages/hub/TournamentHubDetailPage';
import TournamentHubAdminPage from './pages/TournamentHubAdminPage';
// Customer Portal Pages
import CustomerLoginPage from './pages/play/CustomerLoginPage';
import ProfileSetupPage from './pages/play/ProfileSetupPage';
import CustomerHomePage from './pages/play/CustomerHomePage';
import VenueSelectionPage from './pages/play/VenueSelectionPage';
import SlotSelectionPage from './pages/play/SlotSelectionPage';
import BookingConfirmPage from './pages/play/BookingConfirmPage';
import PaymentStatusPage from './pages/play/PaymentStatusPage';
import MyBookingsPage from './pages/play/MyBookingsPage';
import CustomerProfilePage from './pages/play/CustomerProfilePage';
import './App.css';

// Wrapper to show CustomerNavBar on player routes (non-office routes)
function CustomerNavWrapper({ children }) {
  const location = useLocation();
  const isPlayerRoute = !location.pathname.startsWith('/office');

  // Capture ?discount=CODE from share links and persist it until the
  // booking confirmation page can auto-apply it.
  React.useEffect(() => {
    const code = new URLSearchParams(location.search).get('discount');
    if (code && code.trim()) {
      localStorage.setItem('pending_discount_code', code.trim().toUpperCase());
    }
  }, [location.search]);

  // Player analytics: track page views on route change + meaningful clicks.
  // Mounted here so it only ever runs on player-facing (non-/office) routes.
  React.useEffect(() => {
    if (isPlayerRoute) trackPageView(location.pathname);
  }, [location.pathname, isPlayerRoute]);

  React.useEffect(() => {
    if (!isPlayerRoute) return undefined;
    const handler = (e) => {
      const el = e.target.closest('a,button,[data-testid]');
      if (!el) return;
      const label = el.getAttribute('data-testid') || (el.innerText || '').trim() || el.tagName;
      trackClick(label);
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [isPlayerRoute]);

  return (
    <>
      {isPlayerRoute && <CustomerNavBar />}
      {children}
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <MasterDataProvider>
        <CustomerAuthProvider>
          <BrowserRouter>
            <Routes>
              {/* Admin Routes (/office/*) */}
              <Route path="/office" element={<Navigate to="/office/login" replace />} />
              <Route path="/office/login" element={<LoginPage />} />
              <Route path="/office/register" element={<RegisterPage />} />
              {/* Public expense approval link (no login) */}
              <Route path="/expenses/approve/:requestId" element={<PublicExpenseApprovalPage />} />
              <Route
                path="/office/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/bookings"
                element={
                  <ProtectedRoute>
                    <BookingsListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/bookings/new"
                element={
                  <ProtectedRoute>
                    <NewBookingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/contacts"
                element={
                  <ProtectedRoute>
                    <ContactsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/analysis"
                element={
                  <ProtectedRoute>
                    <AnalysisPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/email-bookings"
                element={
                  <ProtectedRoute>
                    <EmailBookingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/events"
                element={
                  <ProtectedRoute>
                    <EventsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/reports"
                element={
                  <ProtectedRoute>
                    <ReportsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/slot-finder"
                element={
                  <ProtectedRoute>
                    <SlotFinderPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/slot-inventory"
                element={
                  <ProtectedRoute>
                    <SlotInventoryPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/users"
                element={
                  <ProtectedRoute>
                    <UsersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/calendar"
                element={
                  <ProtectedRoute>
                    <CalendarViewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/whatsapp-booking"
                element={
                  <ProtectedRoute>
                    <WhatsAppBookingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/login-logs"
                element={
                  <ProtectedRoute>
                    <LoginLogsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/venue-court-master"
                element={
                  <ProtectedRoute>
                    <VenueCourtMasterPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/pricing-dashboard"
                element={
                  <ProtectedRoute>
                    <PricingDashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/coupons"
                element={
                  <ProtectedRoute>
                    <CouponManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/announcements"
                element={
                  <ProtectedRoute>
                    <AnnouncementsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/working-hours"
                element={
                  <ProtectedRoute>
                    <WorkingHoursPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/settings"
                element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/tournaments"
                element={<Navigate to="/office/tournament-hub" replace />}
              />
              <Route
                path="/office/knowledge-base"
                element={
                  <ProtectedRoute>
                    <KnowledgeBasePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/whatsapp-analytics"
                element={
                  <ProtectedRoute>
                    <WhatsAppAnalyticsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/whatsapp-conversations"
                element={
                  <ProtectedRoute>
                    <WhatsAppConversationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/template-logs"
                element={
                  <ProtectedRoute>
                    <TemplateLogsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/occupancy"
                element={
                  <ProtectedRoute>
                    <OccupancyPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/slot-booking"
                element={
                  <ProtectedRoute>
                    <SlotBookingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/cancellations"
                element={
                  <ProtectedRoute>
                    <CancellationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/day-book"
                element={
                  <ProtectedRoute>
                    <DayBookEntryPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/daily-sales-report"
                element={
                  <ProtectedRoute>
                    <DailySalesReportPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/pos-players"
                element={
                  <ProtectedRoute>
                    <PosPlayersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/calling-data"
                element={
                  <ProtectedRoute>
                    <CallingDataPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/expenses"
                element={
                  <ProtectedRoute>
                    <ExpensesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/duty-roster"
                element={
                  <ProtectedRoute>
                    <DutyRosterPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/pnl"
                element={
                  <ProtectedRoute requireAdmin>
                    <PnLPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/funds"
                element={
                  <ProtectedRoute>
                    <FundsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/venue-events"
                element={
                  <ProtectedRoute>
                    <VenueEventsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/app-users"
                element={
                  <ProtectedRoute>
                    <AppUsersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/event-players"
                element={
                  <ProtectedRoute>
                    <EventPlayersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/coaching"
                element={
                  <ProtectedRoute>
                    <CoachingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/school"
                element={
                  <ProtectedRoute>
                    <SchoolPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/marketing"
                element={
                  <ProtectedRoute>
                    <MarketingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/wa-campaigns"
                element={
                  <ProtectedRoute>
                    <WaCampaignPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/voice-api"
                element={
                  <ProtectedRoute>
                    <VoiceApiPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/review-log"
                element={
                  <ProtectedRoute>
                    <ReviewLogPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/public-api-logs"
                element={
                  <ProtectedRoute>
                    <PublicApiLogsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/venue-aliases"
                element={
                  <ProtectedRoute>
                    <VenueAliasesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/stats"
                element={
                  <ProtectedRoute>
                    <AnalyticsDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/mgmt"
                element={
                  <ProtectedRoute>
                    <MgmtDashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/office/tournament-hub"
                element={
                  <ProtectedRoute>
                    <TournamentHubAdminPage />
                  </ProtectedRoute>
                }
              />

              {/* Tournament Hub (public directory - no login required) */}
              <Route path="/hub" element={<TournamentHubPage />} />
              <Route path="/hub/submit" element={<TournamentHubSubmitPage />} />
              <Route path="/hub/:id" element={<TournamentHubDetailPage />} />
              
              {/* Customer Portal Routes (root) */}
              <Route path="/login" element={<CustomerNavWrapper><CustomerLoginPage /></CustomerNavWrapper>} />
              <Route 
                path="/profile-setup" 
                element={
                  <CustomerProtectedRoute>
                    <CustomerNavWrapper><ProfileSetupPage /></CustomerNavWrapper>
                  </CustomerProtectedRoute>
                } 
              />
              <Route 
                path="/" 
                element={
                  <CustomerProtectedRoute>
                    <CustomerNavWrapper><CustomerHomePage /></CustomerNavWrapper>
                  </CustomerProtectedRoute>
                } 
              />
              <Route 
                path="/venues" 
                element={
                  <CustomerProtectedRoute>
                    <CustomerNavWrapper><VenueSelectionPage /></CustomerNavWrapper>
                  </CustomerProtectedRoute>
                } 
              />
              <Route 
                path="/slots" 
                element={
                  <CustomerProtectedRoute>
                    <CustomerNavWrapper><SlotSelectionPage /></CustomerNavWrapper>
                  </CustomerProtectedRoute>
                } 
              />
              <Route 
                path="/confirm" 
                element={
                  <CustomerProtectedRoute>
                    <CustomerNavWrapper><BookingConfirmPage /></CustomerNavWrapper>
                  </CustomerProtectedRoute>
                } 
              />
              <Route 
                path="/payment-status" 
                element={
                  <CustomerProtectedRoute>
                    <PaymentStatusPage />
                  </CustomerProtectedRoute>
                } 
              />
              <Route 
                path="/my-bookings" 
                element={
                  <CustomerProtectedRoute>
                    <CustomerNavWrapper><MyBookingsPage /></CustomerNavWrapper>
                  </CustomerProtectedRoute>
                } 
              />
              <Route 
                path="/profile" 
                element={
                  <CustomerProtectedRoute>
                    <CustomerNavWrapper><CustomerProfilePage /></CustomerNavWrapper>
                  </CustomerProtectedRoute>
                } 
              />

              {/* WhatsApp Payment Page (public - no auth required) */}
              <Route path="/pay" element={<WhatsAppPayPage />} />

              {/* Public shareable event landing (no login required to view) */}
              <Route path="/e/:id" element={<PublicEventLandingPage />} />

              {/* Player-facing Events (Tournaments) */}
              <Route path="/events" element={<CustomerProtectedRoute><CustomerNavWrapper><PlayerEventsPage /></CustomerNavWrapper></CustomerProtectedRoute>} />
              <Route path="/events/payment-status" element={<CustomerProtectedRoute><EventPaymentStatusPage /></CustomerProtectedRoute>} />
              <Route path="/events/:id" element={<CustomerNavWrapper><PlayerEventDetailPage /></CustomerNavWrapper>} />
              <Route path="/my-registrations" element={<CustomerProtectedRoute><CustomerNavWrapper><MyRegistrationsPage /></CustomerNavWrapper></CustomerProtectedRoute>} />

              {/* Redirects: Old /play/* paths → new root paths */}
              <Route path="/play" element={<Navigate to="/" replace />} />
              <Route path="/play/login" element={<Navigate to="/login" replace />} />
              <Route path="/play/profile-setup" element={<Navigate to="/profile-setup" replace />} />
              <Route path="/play/venues" element={<Navigate to="/venues" replace />} />
              <Route path="/play/slots" element={<Navigate to="/slots" replace />} />
              <Route path="/play/confirm" element={<Navigate to="/confirm" replace />} />
              <Route path="/play/payment-status" element={<Navigate to="/payment-status" replace />} />
              <Route path="/play/my-bookings" element={<Navigate to="/my-bookings" replace />} />
              <Route path="/play/profile" element={<Navigate to="/profile" replace />} />
            </Routes>
            <Toaster position="top-right" richColors />
            <PWAInstallPrompt />
          </BrowserRouter>
        </CustomerAuthProvider>
      </MasterDataProvider>
    </AuthProvider>
  );
}

export default App;